import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, saveAuthTokens } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * "Continue with Apple" — mandatory on iOS whenever Google sign-in is offered.
 *  • iOS app:  native ASAuthorization sheet via @capacitor-community/apple-sign-in
 *  • Web:      Apple JS popup (same plugin, web impl) using the Services ID
 *  • Android:  hidden (Apple's plugin does not support Android)
 * Both paths return an identity token that /api/auth/apple verifies via Apple JWKS.
 */
const REDIRECT_URI = "https://bidorah.com/auth/apple";

const randomToken = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

export default function AppleSignInButton({ label = "Continue with Apple", testid }) {
  const [platform, setPlatform] = useState(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        setPlatform(Capacitor.getPlatform());
      } catch (_) {
        setPlatform("web");
      }
    })();
  }, []);

  const isIOS = platform === "ios";
  const webClientId = process.env.REACT_APP_APPLE_SERVICE_ID;
  if (!platform || platform === "android" || (!isIOS && !webClientId)) return null;

  const start = async () => {
    if (!isIOS && !window.location.hostname.endsWith("bidorah.com")) {
      toast.info("Apple Sign-In only works on production (bidorah.com). Use email + password on preview.", { duration: 6000 });
      return;
    }
    const clientId = isIOS ? process.env.REACT_APP_APPLE_IOS_BUNDLE_ID : webClientId;
    const nonce = randomToken();
    setBusy(true);
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const { response } = await SignInWithApple.authorize({
        clientId,
        redirectURI: REDIRECT_URI,
        scopes: "email name",
        state: randomToken(),
        nonce,
      });
      const { data } = await api.post("/auth/apple", {
        identity_token: response.identityToken,
        nonce,
        email: response.email || null,
        given_name: response.givenName || null,
        family_name: response.familyName || null,
      });
      saveAuthTokens(data);
      setUser(data);
      toast.success(data.is_new ? `Welcome to Bidorah, ${data.name}!` : `Welcome back, ${data.name}`);
      navigate(data.is_new ? "/verify" : "/dashboard", { replace: true });
    } catch (err) {
      const msg = String(err?.message || err || "");
      // ASAuthorizationError.canceled (1001) / Apple JS popup_closed_by_user — user backed out.
      if (/1001|cancel|popup_closed/i.test(msg)) return;
      toast.error(err?.response ? apiError(err) : "Apple sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={start}
      disabled={busy}
      className="w-full h-11 rounded-md bg-black hover:bg-zinc-800 disabled:opacity-60 text-white font-semibold text-sm flex items-center justify-center gap-3 transition-colors shadow-sm"
      data-testid={testid || "apple-signin-btn"}
      aria-label={label}
    >
      <AppleGlyph />
      <span>{busy ? "Connecting…" : label}</span>
    </button>
  );
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
    </svg>
  );
}
